<template>
  <a-col
    v-if="isCompleted && competenceSetList.length > 0"
    class="form-competencies"
  >
    <template
      v-for="(competenceSet, index) in competenceSetList"
      :key="competenceSet.gridId"
    >
      <div @click="handleCompetenceClick(index)">
        <div class="competence-title">
          Компетенции {{ index + 1 }}
          <c-button-remove
            v-if="!disabled && competenceSetList.length > 1"
            class="button-remove"
            @click="removeCompetenceSet(index)"
          />
        </div>

        <div v-if="getArrayFieldError(index, 'id')" class="set_id_error">
          {{ getArrayFieldError(index, 'id') }}
        </div>

        <competence-set
          :index="index"
          :competence-set="competenceSet"
          :competence-options="competenceOptions"
          :api-errors="getArrayApiErrors(index)"
          :disabled="disabled"
          @update="updateCompetencies"
        />
      </div>

      <a-divider
        class="divider"
        v-if="
          index !== 2 &&
          index !== competenceSetList[competenceSetList.length - 1]
        "
      />
    </template>

    <c-button-add
      v-if="!disabled && competenceSetList.length !== MAX_COMPETENCE_SET"
      class="actions"
      @click="addCompetency"
    >
      Добавить компетенции
    </c-button-add>
  </a-col>
</template>

<script setup>
  import { onMounted, ref, watch } from 'vue';
  import { useApi } from '@/api/use-api';
  import CButtonAdd from '@/components/UI/CButtonAdd.vue';
  import CompetenceSet from '@/components/projects/management/Competencies/CompetenceSet.vue';
  import { useRoute } from 'vue-router';
  import { useArrayErrors } from '@/composables/useArrayErrors';
  import CButtonRemove from '@/components/UI/CButtonRemove.vue';
  import { USER_ROLE_LEADER } from '@/api/models/UserRole';
  import { useCompetenceOptions } from '@/composables/useCompetenceOptions';
  import { MAX_COMPETENCE_SET } from '@/constants/competence-set';

  const props = defineProps({
    checkpoint: Object,
    isCompleted: Boolean,
    disabled: {
      type: Boolean,
      default: true,
    },
  });

  const api = useApi();
  const route = useRoute();
  const { competenceOptions } = useCompetenceOptions(
    props.checkpoint.projectId
  );
  const emit = defineEmits(['update', 'saved']);
  const competenciesChanged = ref(false);
  const competenceSetList = ref([]);
  const gridId = ref(0);

  onMounted(async () => {
    await getCompetenceSetList();
  });

  watch(
    () => props.isCompleted,
    () => {
      if (props.isCompleted) {
        if (competenceSetList.value.length === 0) {
          addCompetency();
        } else {
          competenceSetList.value.forEach((el) => {
            el.confirmedAt = null;
            el.rejectedAt = null;
          });
        }
      }
    }
  );

  const { getArrayFieldError, handleArrayErrors, getArrayApiErrors } =
    useArrayErrors('competenceSetList', competenceSetList);

  const getCompetenceSetList = async () => {
    try {
      const searchParams = new URLSearchParams({
        checkpointId: props.checkpoint.id,
      });

      const response =
        route.meta.role === USER_ROLE_LEADER
          ? await api.checkpointCompetenceSet.index(searchParams)
          : await api.checkpointCompetenceSet.performerCompetencies(
              searchParams
            );
      competenceSetList.value = response.sets.items.map((el, index) => {
        gridId.value = index;
        return { gridId: index, ...el };
      });

      if (!props.disabled && competenceSetList.value.length === 0) {
        addCompetency();
      }
    } catch (error) {
      console.error('error', error);
    }
  };

  const onFinish = async () => {
    const competenceSetList = prepareSetList();

    try {
      await api.checkpointCompetenceSet.saveCheckpointCompetencies({
        checkpointId: props.checkpoint.id,
        competenceSetList: competenceSetList,
      });

      emit('saved', true);
    } catch (error) {
      handleArrayErrors(error);
      emit('saved', false);
    }
  };
  defineExpose({ onFinish });

  const addCompetency = () => {
    if (competenceSetList.value.length === MAX_COMPETENCE_SET) return;

    competenceSetList.value.push({
      gridId: gridId.value,
      id: null,
      checkpointId: props.checkpoint.id,
      confirmedAt: null,
      rejectedAt: null,
      competenceSubject: null,
      competenceTechnology: null,
      competenceSkill: null,
    });
    gridId.value++;
  };

  const removeCompetenceSet = (id) => {
    competenceSetList.value.splice(id, 1);
  };

  const updateCompetencies = (value) => {
    competenceSetList.value[value.index] = {
      ...competenceSetList.value[value.index],
      ...value.competenceSet,
    };
  };

  /*
   * Если компетенции оценивались, то:
   *   - удаляем отметку об отклонении, если компетенция согласована,
   *   - если нет отметок о согласовании и отклонении, то отклоняем компетенцию.
   * Удалаем последний элемент competenceSetList.value перед отправкой, если все компетенции null
   */
  const prepareSetList = () => {
    if (competenciesChanged.value) {
      competenceSetList.value.forEach((el) => {
        if (el.confirmedAt) {
          el.rejectedAt = null;
        }
        if (!el.confirmedAt && !el.rejectedAt) {
          el.rejectedAt = Math.floor(new Date().getTime() / 1000);
        }
      });
    }

    const lastSet = competenceSetList.value[competenceSetList.value.length - 1];
    return lastSet.competenceSubject === null &&
      lastSet.competenceTechnology === null &&
      lastSet.competenceSkill === null
      ? competenceSetList.value.slice(0, -1)
      : competenceSetList.value;
  };

  const handleCompetenceClick = (index) => {
    if (route.meta.role !== USER_ROLE_LEADER || !props.disabled) return;

    competenciesChanged.value = true;
    emit('update');

    const set = competenceSetList.value[index];
    if (set.confirmedAt || set.rejectedAt) {
      set.confirmedAt = null;
      set.rejectedAt = null;
      set.status = 'default';
    } else {
      set.confirmedAt = Math.floor(new Date().getTime() / 1000);
      set.rejectedAt = null;
      set.status = 'confirmed';
    }
  };
</script>

<style lang="scss" scoped>
  @use "@/assets/styles/_colors.scss" as *;

  .form-competencies {
    background: $color-bg-secondary-grey;
    border-radius: 6px;
    flex-direction: column;
    margin-top: 30px;
    padding: 20px;
    min-height: 216px;
    height: 100%;

    .competence-title {
      color: $color-bg-status-new;
      font: 700 14px/20px 'Nunito Sans';
      margin-bottom: 20px;

      .button-remove {
        margin-left: 10px;
      }
    }

    .set_id_error {
      color: $color-text-accent-red;
      margin-bottom: 14px;
    }

    .actions {
      margin-top: 30px;
    }

    .divider {
      border-color: $color-bg-primary-white;
    }
  }
</style>
